<template>
  <div class="fixed inset-0 bg-black z-full-screen-overlay" @click.stop>
    <Header
      :title="title ? title : $t('Add drawing')"
      :show-save-draft="allowDrawing"
      @cancel="cancel"
      @save="save"
    />

    <div ref="canvasContainer" class="fixed inset-0 flex items-center justify-center canvas"></div>

    <div v-if="allowDrawing && colors.length > 1" class="absolute" style="left:1rem; bottom:1rem;">
      <template v-for="color in colors">
        <button
          :key="color.color"
          class="rounded-lg inline-block px-3 mr-3 align-bottom"
          style="background-color:rgba(0,0,0,0.5); height: 44px; width: 44px; padding-left: 10px; padding-right: 10px;"
          :style="{
                    'background-color': color.color,
                    'color': color.marker,
                }"
          @click="penColor = color.color"
        >
          <CheckIcon v-if="penColor === color.color" />
        </button>
      </template>
    </div>

    <button
      v-if="allowDrawing"
      class="text-white rounded-lg absolute inline-block px-3"
      style="background-color:rgba(0,0,0,0.5); right:1rem; bottom:1rem; height: 44px;"
      @click="reset"
    >
      {{ removeButtonTitle ? removeButtonTitle : $t('Remove drawing') }}
    </button>
  </div>
</template>

<script>
  import SignaturePad from 'signature_pad';
  import Header from '@/_legacy/components/DrawablePhoto/Header.vue';
  import CheckIcon from '@/_legacy/components/Icons/CheckIcon.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import { dataURLtoBlob } from '@/_legacy/util/blob';

  let canvas;

  export default {
    components: {
      Header,
      CheckIcon,
    },
    props: {
      image: {
        type: String,
        default: null,
      },
      drawing: {
        type: String,
        default: null,
      },
      width: {
        type: Number,
        default: null,
      },
      height: {
        type: Number,
        default: null,
      },
      title: {
        type: String,
        default: null,
      },
      removeButtonTitle: {
        type: String,
        default: null,
      },
      colors: {
        type: Array,
        default: () => [
          {
            color: '#ff6e44',
            marker: '#ffffff',
          },
          {
            color: '#000000',
            marker: '#ffffff',
          },
          {
            color: '#ffffff',
            marker: '#0000000',
          },
        ],
      },
      allowDrawing: {
        type: Boolean,
        default: true,
      },
    },
    data: () => ({
      signaturePad: {},
      penColor: null,
      imageBlobURL: null,
      drawingBlobURL: null,
    }),
    watch: {
      image: {
        handler(image) {
          if (image) {
            this.imageBlobURL = URL.createObjectURL(dataURLtoBlob(image));
          }
        },
        immediate: true,
      },
      drawing: {
        handler(drawing) {
          if (drawing) {
            this.drawingBlobURL = URL.createObjectURL(dataURLtoBlob(drawing));
          }
        },
        immediate: true,
      },
      penColor(penColor) {
        this.signaturePad.penColor = penColor;
      },
    },
    created() {
      this.penColor = this.colors[0].color;
      eventBus.$emit('modalOpen');
    },
    mounted() {
      if (this.width === null || this.height === null) {
        const i = new Image();
        i.onload = function() {
          this.start(i.width, i.height);
        }.bind(this);
        i.src = this.imageBlobURL;
      } else {
        this.start(this.width, this.height);
      }
    },
    beforeDestroy() {
      this.cleanup();
    },
    methods: {
      setScale() {
        const ratio = canvas.width / canvas.offsetWidth;
        canvas.getContext('2d').resetTransform();
        canvas.getContext('2d').scale(ratio, ratio);
      },
      start(width, height) {
        canvas = document.createElement('canvas');

        if (width < height) {
          canvas.height = Math.min(height, 960);
          canvas.width = parseInt(canvas.height * width / height, 10);
        } else {
          canvas.width = Math.min(width, 960);
          canvas.height = parseInt(canvas.width * height / width, 10);
        }

        canvas.setAttribute('style', `background-color: none; background-image: url(${this.imageBlobURL}); background-repeat: no-repeat; background-size: 100% 100%; max-width: 100%; max-height: 100%`);

        this.$refs.canvasContainer.append(canvas);

        this.setScale();
        window.addEventListener('resize', this.setScale);
        this.signaturePad = new SignaturePad(canvas, {
          dotSize: (0.5 + 2.5) / 2,
          minWidth: 0.5,
          maxWidth: 2.5,
          throttle: 16,
          minDistance: 5,
          backgroundColor: 'rgba(0,0,0,0)',
          penColor: this.penColor,
          velocityFilterWeight: 0.7,
          onBegin: () => {},
          onEnd: () => {},
        });
        if (this.drawing !== null) {
          this.signaturePad.fromDataURL(this.drawing, {
            ratio: 1,
            width: canvas.offsetWidth,
            height: canvas.offsetHeight,
          });
        }

        if (!this.allowDrawing) {
          this.signaturePad.off();
          canvas.style.pointerEvents = 'none';
          canvas.style.cursor = 'default';
        }
      },
      save() {
        let drawing = null;
        if (this.allowDrawing && this.signaturePad && !this.signaturePad.isEmpty()) {
          drawing = this.signaturePad.toDataURL('image/png');
        }
        this.cleanup();
        this.$emit('change', drawing);
      },
      cancel() {
        this.cleanup();
        this.$emit('cancel');
      },
      cleanup() {
        eventBus.$emit('modalClose');

        if (this.imageBlobURL) {
          URL.revokeObjectURL(this.imageBlobURL);
        }

        if (this.drawingBlobURL) {
          URL.revokeObjectURL(this.drawingBlobURL);
        }

        window.removeEventListener('resize', this.setScale);

        if (this.signaturePad) {
          this.signaturePad.off();
          this.signaturePad = null;
        }

        if (canvas) {
          // https://bugs.webkit.org/show_bug.cgi?id=195325
          canvas.width = 0;
          canvas.height = 0;
          this.$refs.canvasContainer.removeChild(canvas);
          canvas = null;
        }
      },
      reset() {
        this.signaturePad.clear();
      },
    },
  };
</script>

<style scoped>
  .canvas {
    --headerHeight: 61px;
    --footerHeight: 61px;

    height: calc(100% - (var(--headerHeight) + var(--footerHeight)));
    margin-top: var(--headerHeight);
  }
</style>
